import { t } from 'i18next'
import { AutoComplete, TextField } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import {
  ColumnType,
  ResourceType,
  SeriesType,
  TimelineType,
} from '@pages/DiagramsCreateOrEdit/types'
import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import {
  axisXOptions,
  FORMATS_DATE_OPTIONS,
  GENERATOR_INPUT_TYPE,
  incrementOptions,
} from '@constants'
import { AutocompleteOption, INCREMENT_TYPE } from '@types'

type useInputsParams = {
  watchTimeline: TimelineType
  watchSeries: SeriesType[]
  watchResources: ResourceType[]
  objects: AutocompleteOption[]
  viewsOptions: AutocompleteOption[][]
  handleViewChange: (index: number, value: AutocompleteOption, columnId: string) => void
  watchColumns: ColumnType[]
  handleChangeSeries: () => void
}

export const useInputs = ({
  watchTimeline,
  watchSeries,
  watchResources,
  objects,
  viewsOptions,
  handleViewChange,
  watchColumns,
  handleChangeSeries,
}: useInputsParams) => {
  const timelineInputs: FormInputsType[] = [
    {
      name: `timeline.axisX`,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: t('diagramsCreateOrEdit.axisX.axisX'),
      autocompleteOptions: axisXOptions,
      disabled: true,
    },
    {
      name: 'timeline.dateRange',
      label: t('diagramsCreateOrEdit.axisX.dateRange'),
      inputType: GENERATOR_INPUT_TYPE.DATE_RANGE_PICKER,
      value: watchTimeline.dateRange,
      formatDate: [INCREMENT_TYPE.HOUR, INCREMENT_TYPE.MINUTE].includes(
        watchTimeline.timeIncrement.id
      )
        ? 'DD.MM.YYYY HH:mm'
        : 'DD.MM.YYYY',
      dateTimePickerViews: [INCREMENT_TYPE.HOUR, INCREMENT_TYPE.MINUTE].includes(
        watchTimeline.timeIncrement.id
      )
        ? undefined
        : ['day'],
    },
    {
      name: 'timeline.timeIncrement',
      label: t('diagramsCreateOrEdit.axisX.timeIncrement'),
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      autocompleteOptions: incrementOptions,
    },
    {
      name: 'timeline.formatDate',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: t('diagramsCreateOrEdit.axisX.formatDate'),
      autocompleteOptions: FORMATS_DATE_OPTIONS,
      selectValue: 'id',
    },
    {
      name: 'timeline.hideFilters',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: 'Hide roster filters',
    },
  ]

  const renderViewInputs = () =>
    watchColumns?.map((column, index) => {
      const resourceIndex = watchResources.findIndex(resource => resource.columnId == column.id)
      const resource = resourceIndex !== -1 ? watchResources[resourceIndex] : null

      return (
        <Box key={resource?.columnId} alignItems={'center'} display={'flex'} flex={0}>
          <Typography
            title={column.title}
            sx={theme => ({
              width: 150,
              color: theme.palette.grey[700],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {column.title}
          </Typography>
          <AutoComplete
            getOptionLabel={option => option?.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            options={viewsOptions[index]}
            placeholder={t('diagramsCreateOrEdit.view.view')}
            size={'small'}
            sx={{ flex: 1, paddingY: 1 }}
            defaultValue={
              resource?.viewId ? { label: resource.viewTitle, id: resource.viewId } : null
            }
            renderInput={params => {
              return <TextField {...params} sx={{ flex: 1 }} />
            }}
            onChange={value =>
              handleViewChange(
                resource ? resourceIndex : watchResources.length,
                value,
                resource?.columnId || column.id
              )
            }
          />
        </Box>
      )
    }) || []

  return {
    timelineInputs,
    renderViewInputs,
  }
}
